import { useState } from "react";
import type { FormEvent } from "react";

type FormStatus = { type: "idle" | "success" | "error"; msg: string };

export default function Subscribe() {
  const [subStatus, setSubStatus] = useState<FormStatus>({ type: "idle", msg: "" });
  const [tipStatus, setTipStatus] = useState<FormStatus>({ type: "idle", msg: "" });
  const [loading, setLoading] = useState<"sub" | "tip" | null>(null);

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const hp = data.get("contact_alt")?.toString().trim() || "";
    if (hp) {
      setSubStatus({ type: "success", msg: "订阅成功，感谢关注吃瓜51新瓜速递！" });
      form.reset();
      return;
    }
    data.delete("contact_alt");
    setLoading("sub");
    setSubStatus({ type: "idle", msg: "" });
    try {
      const res = await fetch("https://readdy.ai/api/form/daetlm9tto0ceqqnh3p0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      const parsed = safeParse(text);
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      const ok =
        res.ok &&
        (parsed?.code === "OK" || parsed?.code === "ok") &&
        !(serverMsg && /spam/i.test(serverMsg));
      if (ok) {
        setSubStatus({ type: "success", msg: "订阅成功，今日新瓜简报将发送至你的邮箱！" });
        form.reset();
      } else {
        setSubStatus({ type: "error", msg: serverMsg || "订阅失败，请稍后再试。" });
      }
    } catch {
      setSubStatus({ type: "error", msg: "网络异常，订阅未成功，请稍后重试。" });
    } finally {
      setLoading(null);
    }
  };

  const handleTip = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const hp = data.get("website_alt")?.toString().trim() || "";
    if (hp) {
      setTipStatus({ type: "success", msg: "投稿已收到，感谢你的爆料线索！" });
      form.reset();
      return;
    }
    data.delete("website_alt");
    setLoading("tip");
    setTipStatus({ type: "idle", msg: "" });
    try {
      const res = await fetch("https://readdy.ai/api/form/daetlm9tto0ceqqnh3pg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      const parsed = safeParse(text);
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      const ok =
        res.ok &&
        (parsed?.code === "OK" || parsed?.code === "ok") &&
        !(serverMsg && /spam/i.test(serverMsg));
      if (ok) {
        setTipStatus({ type: "success", msg: "投稿已收到，经核实后我们会优先收录！" });
        form.reset();
      } else {
        setTipStatus({ type: "error", msg: serverMsg || "投稿失败，请稍后再试。" });
      }
    } catch {
      setTipStatus({ type: "error", msg: "网络异常，投稿未成功，请稍后重试。" });
    } finally {
      setLoading(null);
    }
  };

  return (
    <section id="subscribe" className="scroll-mt-20 relative overflow-hidden bg-primary-600 py-14 md:py-20">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-background-50/10 blur-3xl" />
        <div className="absolute -left-16 bottom-0 h-64 w-64 rounded-full bg-accent-500/30 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-12">
          {/* 订阅 */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-mail-send-fill" />
              每日简报
            </span>
            <h2 className="mt-4 font-heading text-3xl font-black text-background-50 md:text-4xl">
              订阅新瓜速递
            </h2>
            <p className="mt-3 max-w-lg text-base leading-relaxed text-background-50/90">
              留下邮箱，每日热榜与精选话题整理成一份简报，及时送到你的收件箱，再也不错过今天的新瓜。
            </p>

            <form
              data-readdy-form="daetlm9tto0ceqqnh3p0"
              onSubmit={handleSubscribe}
              className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <input
                type="email"
                name="email"
                required
                placeholder="请输入你的邮箱地址"
                className="h-12 w-full rounded-lg border border-background-50/25 bg-background-50/15 px-4 text-sm text-background-50 placeholder-background-50/60 outline-none transition focus:border-background-50/70 focus:bg-background-50/25"
              />
              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={loading === "sub"}
                className="flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-accent-500 px-6 text-base font-bold text-foreground-950 transition-colors hover:bg-accent-400 disabled:opacity-60"
              >
                {loading === "sub" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" />
                    立即订阅
                  </>
                )}
              </button>
            </form>
            {subStatus.type !== "idle" && (
              <StatusLine status={subStatus} />
            )}
            <p className="mt-3 text-xs text-background-50/70">
              订阅即表示同意接收吃瓜51的内容简报，可随时退订。
            </p>
          </div>

          {/* 爆料投稿 */}
          <div className="rounded-2xl bg-background-50 p-6 md:p-8">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-megaphone-fill text-primary-500" />
                爆料投稿
              </h3>
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800">
                <i className="ri-flashlight-fill" />
                优先收录
              </span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              发现新鲜事或有价值线索？填写下方表单，经核实整理后将收录进对应栏目。
            </p>

            <form
              data-readdy-form="daetlm9tto0ceqqnh3pg"
              onSubmit={handleTip}
              className="mt-5 space-y-3"
            >
              <input
                type="text"
                name="name"
                placeholder="你的称呼（选填）"
                className="h-11 w-full rounded-lg border border-background-200 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
              <input
                type="email"
                name="email"
                placeholder="联系邮箱（选填）"
                className="h-11 w-full rounded-lg border border-background-200 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
              <textarea
                name="message"
                required
                maxLength={500}
                rows={4}
                placeholder="简要描述你掌握的线索或新鲜事……"
                className="w-full resize-none rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-xs text-foreground-600">
                  <i className="ri-lock-line" />
                  信息仅用于内容核实，将严格保密
                </span>
                <button
                  type="submit"
                  disabled={loading === "tip"}
                  className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {loading === "tip" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中
                    </>
                  ) : (
                    <>
                      <i className="ri-megaphone-fill" />
                      提交线索
                    </>
                  )}
                </button>
              </div>
              {tipStatus.type !== "idle" && (
                <StatusLine status={tipStatus} />
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatusLine({ status }: { status: FormStatus }) {
  const ok = status.type === "success";
  return (
    <p
      className={`mt-3 flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ${
        ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
      }`}
    >
      <i className={ok ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"} />
      {status.msg}
    </p>
  );
}

function safeParse(text: string): Record<string, unknown> | null {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}